import React, { useContext, useEffect, useState } from 'react';
import { Box, Collapse, List, ListItemText, Divider, ListItemButton } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { USER_ROLES } from '../constants';

const AI_SECTION_PATHS = ['/ai-settings', '/ai-logs', '/company-profile-setup', '/knowledge-base'];

const navItemSx = (active) => ({
  color: active ? '#1976d2' : '#333',
});

const nestedItemSx = (active) => ({
  pl: 4,
  py: 0.75,
  color: active ? '#1976d2' : '#333',
  bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
  borderRadius: 1,
  mx: 1,
  '&:hover': {
    bgcolor: active ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
  },
});

const isPathActive = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

const Sidebar = () => {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const isAgent = user?.role === USER_ROLES.LEAD_AGENT;
  const isClient = user?.role === USER_ROLES.CLIENT;
  const isAdmin = user?.role === USER_ROLES.ADMIN;

  const isAiSettings = isPathActive(location.pathname, '/ai-settings');
  const isAiLogs = isPathActive(location.pathname, '/ai-logs');
  const isCompanyProfileSetup = isPathActive(location.pathname, '/company-profile-setup');
  const isKnowledgeBase = isPathActive(location.pathname, '/knowledge-base');
  const isAiSection = AI_SECTION_PATHS.some((path) => isPathActive(location.pathname, path));

  const [aiSectionOpen, setAiSectionOpen] = useState(isAiSection);

  useEffect(() => {
    if (isAiSection) setAiSectionOpen(true);
  }, [isAiSection]);

  return (
    <Box
      sx={{
        width: '200px',
        height: '100%',
        backgroundColor: '#f5f5f5',
        boxShadow: '2px 0 5px rgba(0, 0, 0, 0.05)',
        paddingTop: '10px',
      }}
      component='nav'
      aria-label='Admin navigation'
    >
      <List>
        <ListItemButton
          component={Link}
          to={isClient ? '/programs' : '/opportunities'}
          sx={navItemSx(location.pathname === '/programs' || location.pathname === '/opportunities')}
        >
          <ListItemText primary={isClient ? 'Programs' : 'Opportunities'} />
        </ListItemButton>
        {(isClient || isAgent) && (
          <>
            <Divider sx={{ borderColor: '#ddd' }} />
            <ListItemButton component={Link} to='/prospects' sx={navItemSx(location.pathname === '/prospects')}>
              <ListItemText primary='Prospects' />
            </ListItemButton>
          </>
        )}
        <Divider sx={{ borderColor: '#ddd' }} />
        <ListItemButton
          component={Link}
          to={isClient ? '/client-commission-overview' : '/commission-overview'}
          sx={navItemSx(
            (isClient && location.pathname === '/client-commission-overview') ||
              (!isClient && location.pathname === '/commission-overview'),
          )}
        >
          <ListItemText primary='Commissions' />
        </ListItemButton>
        {isAdmin && (
          <>
            <Divider sx={{ borderColor: '#ddd' }} />
            <ListItemButton component={Link} to='/lead-agents' sx={navItemSx(location.pathname === '/lead-agents')}>
              <ListItemText primary='Lead Agents' />
            </ListItemButton>
            <Divider sx={{ borderColor: '#ddd' }} />
            <ListItemButton
              component={Link}
              to='/content'
              sx={navItemSx(location.pathname === '/content' || location.pathname.startsWith('/content/articles'))}
            >
              <ListItemText primary='Content' />
            </ListItemButton>
            <Divider sx={{ borderColor: '#ddd' }} />
            <ListItemButton
              onClick={() => setAiSectionOpen((open) => !open)}
              aria-expanded={aiSectionOpen}
              aria-controls='ai-knowledge-submenu'
              sx={{
                ...navItemSx(isAiSection),
                pr: 1.5,
              }}
            >
              <ListItemText
                primary='AI & Knowledge'
                primaryTypographyProps={{ sx: { lineHeight: 1.3, fontWeight: isAiSection ? 600 : 400 } }}
              />
              {aiSectionOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
            </ListItemButton>
            <Collapse in={aiSectionOpen} timeout='auto' unmountOnExit>
              <List id='ai-knowledge-submenu' component='div' disablePadding>
                <ListItemButton
                  component={Link}
                  to='/ai-settings'
                  selected={isAiSettings}
                  sx={nestedItemSx(isAiSettings)}
                >
                  <ListItemText
                    primary='AI Settings'
                    primaryTypographyProps={{ fontSize: 14, fontWeight: isAiSettings ? 600 : 400 }}
                  />
                </ListItemButton>
                <ListItemButton component={Link} to='/ai-logs' selected={isAiLogs} sx={nestedItemSx(isAiLogs)}>
                  <ListItemText
                    primary='AI Logs'
                    primaryTypographyProps={{ fontSize: 14, fontWeight: isAiLogs ? 600 : 400 }}
                  />
                </ListItemButton>
                <ListItemButton
                  component={Link}
                  to='/company-profile-setup'
                  selected={isCompanyProfileSetup}
                  sx={nestedItemSx(isCompanyProfileSetup)}
                >
                  <ListItemText
                    primary='Company Profile Setup'
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isCompanyProfileSetup ? 600 : 400,
                      lineHeight: 1.3,
                    }}
                  />
                </ListItemButton>
                <ListItemButton
                  component={Link}
                  to='/knowledge-base'
                  selected={isKnowledgeBase}
                  sx={nestedItemSx(isKnowledgeBase)}
                >
                  <ListItemText
                    primary='Knowledge Base'
                    primaryTypographyProps={{ fontSize: 14, fontWeight: isKnowledgeBase ? 600 : 400 }}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}
        {(isClient || isAgent) && (
          <>
            <Divider sx={{ borderColor: '#ddd' }} />
            <ListItemButton
              component={Link}
              to='/target-list'
              disabled
              sx={navItemSx(location.pathname === '/target-list')}
            >
              <ListItemText primary='Target List' sx={{ opacity: 0.6 }} />
            </ListItemButton>
          </>
        )}
        {isClient && (
          <>
            <Divider sx={{ borderColor: '#ddd' }} />
            <ListItemButton
              component={Link}
              to='/company-profile'
              sx={navItemSx(location.pathname === '/company-profile')}
            >
              <ListItemText primary='Company Profile' />
            </ListItemButton>
          </>
        )}
      </List>
    </Box>
  );
};

export default Sidebar;
